import type { FunnelAnalyticsModalProps } from "@editor/components/AppModalTypes";

import * as React from "react";

import { useModal } from "@editor/hooks/useModal";

import { Modal } from "@replo/design-system/components/modal/Modal";
import { Progress } from "@replo/design-system/components/shadcn/core/progress";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import { Circle, CircleCheck, CircleDot, Flag, Users } from "lucide-react";
import { formatWithCommasAndTwoDecimals } from "replo-utils/lib/math";

import { useFunnelAnalytics } from "../useFunnelAnalytics";

export const FunnelAnalyticsModal: React.FC<FunnelAnalyticsModalProps> = ({
  funnel,
  timeZone,
}) => {
  const { closeModal } = useModal();

  const { funnelRoutes, mainRangeResults, isLoading } = useFunnelAnalytics(
    funnel,
    timeZone,
  );

  const funnelStepsMetrics = funnelRoutes.map((route, index) => {
    const mainRangeResult = mainRangeResults.find(
      (result) => result.urlPath === route,
    );
    return {
      route,
      stepNumber: index + 1,
      sessions: mainRangeResult?.metrics?.unique_sessions?.[0] ?? 0,
    };
  });

  const firstStepSessions = funnelStepsMetrics[0]?.sessions ?? 0;

  return (
    <Modal
      title={`${funnel.name}: Step by Step Analysis`}
      isOpen={true}
      onOpenChange={(open) => {
        if (!open) {
          closeModal({ type: "funnelAnalyticsModal" });
        }
      }}
      size="lg"
    >
      <div className="p-6">
        <div className="space-y-5">
          {funnelStepsMetrics.map((funnelStepMetric) => {
            const { route, stepNumber, sessions } = funnelStepMetric;

            if (isLoading) {
              return <Skeleton key={route} className="h-10 w-full" />;
            }

            const percentage = (sessions / firstStepSessions) * 100;

            const isFirstStep = stepNumber === 1;
            const isLastStep = stepNumber === funnelRoutes.length;

            const circleIcon = () => {
              if (isLastStep) {
                return <CircleCheck size={18} />;
              } else if (isFirstStep) {
                return <CircleDot size={18} />;
              } else {
                return <Circle size={18} />;
              }
            };

            return (
              <div key={route} className="relative">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {circleIcon()}
                    <div className="flex flex-col">
                      <p className="font-medium">Step {stepNumber}</p>
                      <p className="text-sm text-muted">Visits {route}</p>
                    </div>
                  </div>
                  <div className="flex flex-row gap-2">
                    <p className="font-medium">
                      {sessions.toLocaleString()} sessions
                    </p>
                    <Tag
                      size="base"
                      variant={isLastStep ? "success" : "info"}
                      startEnhancer={
                        isLastStep ? <Flag size={14} /> : <Users size={14} />
                      }
                    >
                      {firstStepSessions === 0
                        ? "--"
                        : `${formatWithCommasAndTwoDecimals(percentage, false)}%`}
                    </Tag>
                  </div>
                </div>

                <Progress
                  value={percentage}
                  className="h-3 bg-border"
                  indicatorClassName={twMerge(
                    "bg-primary",
                    // "bg-graph-secondary-e",
                    stepNumber === funnelRoutes.length && "bg-success",
                  )}
                />
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};
